import type { Language } from "./settings";

const STORAGE_KEY = "bgb_language";

type LanguageStorage = Pick<Storage, "getItem" | "setItem">;

function safeStorage(): LanguageStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function isLanguage(value: unknown): value is Language {
  return value === "ru" || value === "en";
}

/** The language the player picked explicitly, or null when they never did. */
export function readStoredLanguage(storage: LanguageStorage | null = safeStorage()): Language | null {
  try {
    const value = storage?.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

export function storeLanguage(language: Language, storage: LanguageStorage | null = safeStorage()): void {
  try {
    storage?.setItem(STORAGE_KEY, language);
  } catch {
    return;
  }
}

/** Russian for a Russian-first browser or platform locale, English for everything else. */
export function detectLanguage(preferred: readonly string[]): Language {
  const first = preferred[0]?.toLowerCase() ?? "";
  return first.startsWith("ru") ? "ru" : "en";
}

export interface StartupLanguageInput {
  stored: Language | null;
  platform: string | null;
  preferred: readonly string[];
}

/**
 * Startup language: the player's explicit choice wins, then the platform SDK
 * locale (Yandex Games), then the browser's first preferred language.
 */
export function resolveStartupLanguage(input: StartupLanguageInput): Language {
  if (input.stored) {
    return input.stored;
  }
  return detectLanguage(input.platform ? [input.platform] : input.preferred);
}
